import type {
  AutomationTestFixtureInputCandidate,
  AutomationTestFixtureTriggerCandidate,
} from '@/types/automations.ts';

export type SyntheticFixturePayloadKind = 'schema_validated' | 'opaque_json' | 'producer_validated';

export const buildSyntheticInputOverrideTemplate = (
  candidate: AutomationTestFixtureInputCandidate,
  payloadKind: SyntheticFixturePayloadKind = candidate.supported_payload_kinds?.includes('producer_validated')
    ? 'producer_validated' : 'opaque_json',
) => {
  if (candidate.supported_payload_kinds && !candidate.supported_payload_kinds.includes(payloadKind)) {
    throw new Error('This fixture kind is not supported by the selected producer.');
  }
  const common = {
    consumer: candidate.consumer,
    input_group_key: candidate.input_group_key,
    input_index: candidate.input_index,
    data: candidate.dataset_type === 'dataframe' ? [] : {},
  };
  if (payloadKind === 'schema_validated') {
    if (!candidate.schema_id || candidate.schema_version == null) {
      throw new Error('This input does not declare a schema-valid fixture contract.');
    }
    return {
      ...common,
      payload_kind: payloadKind,
      schema_id: candidate.schema_id,
      schema_version: candidate.schema_version,
    };
  }
  return {
    ...common,
    payload_kind: payloadKind,
    fixture_label: 'Describe the edge case',
  };
};

export const buildSyntheticTriggerOverrideTemplate = (
  candidate: AutomationTestFixtureTriggerCandidate,
) => ({
  trigger_step_id: candidate.trigger_step_id,
  trigger_id: candidate.trigger_id,
  data: {},
});

const fixtureConsumerKey = (consumer: unknown) => {
  if (!consumer || typeof consumer !== 'object') return 'unknown';
  const record = consumer as Record<string, unknown>;
  if (record.scope === 'root') return JSON.stringify(['root', String(record.step_id ?? '').trim()]);
  if (record.scope === 'local_subflow') {
    return JSON.stringify([
      'local_subflow',
      String(record.call_step_id ?? '').trim(),
      String(record.subflow_id ?? '').trim(),
      String(record.module_step_id ?? '').trim(),
    ]);
  }
  return 'unknown';
};

const fixtureInputSlotIdentity = (
  consumer: unknown,
  inputGroupKey: unknown,
  inputIndex: unknown,
) => JSON.stringify([fixtureConsumerKey(consumer), String(inputGroupKey ?? '').trim(), inputIndex]);

export const syntheticInputSlotKey = (
  item: Pick<AutomationTestFixtureInputCandidate, 'consumer' | 'input_group_key' | 'input_index'>,
) => fixtureInputSlotIdentity(item.consumer, item.input_group_key, item.input_index);

export const appendSyntheticInputOverrideTemplate = (
  currentText: string,
  candidate: AutomationTestFixtureInputCandidate,
  payloadKind: SyntheticFixturePayloadKind = candidate.supported_payload_kinds?.includes('producer_validated')
    ? 'producer_validated' : 'opaque_json',
) => {
  const current = JSON.parse(currentText) as unknown;
  if (!Array.isArray(current)) throw new Error('Synthetic overrides must be a JSON array.');
  const candidateKey = syntheticInputSlotKey(candidate);
  const alreadyPresent = current.some((item) => {
    if (!item || typeof item !== 'object') return false;
    const record = item as Record<string, unknown>;
    return fixtureInputSlotIdentity(
      record.consumer,
      record.input_group_key,
      record.input_index,
    ) === candidateKey;
  });
  return alreadyPresent
    ? currentText
    : JSON.stringify([...current, buildSyntheticInputOverrideTemplate(candidate, payloadKind)], null, 2);
};

export const appendSyntheticTriggerOverrideTemplate = (
  currentText: string,
  candidate: AutomationTestFixtureTriggerCandidate,
) => {
  const current = JSON.parse(currentText) as unknown;
  if (!Array.isArray(current)) throw new Error('Synthetic trigger overrides must be a JSON array.');
  const alreadyPresent = current.some((item) => {
    if (!item || typeof item !== 'object') return false;
    const record = item as Record<string, unknown>;
    return record.trigger_step_id === candidate.trigger_step_id && record.trigger_id === candidate.trigger_id;
  });
  return alreadyPresent
    ? currentText
    : JSON.stringify([...current, buildSyntheticTriggerOverrideTemplate(candidate)], null, 2);
};

const parseOverrideArray = (text: string, label: string): unknown[] => {
  const value = JSON.parse(text) as unknown;
  if (!Array.isArray(value)) {
    throw new Error(`${label} must be a JSON array.`);
  }
  return value;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const requireRecord = (value: unknown, label: string): Record<string, unknown> => {
  if (!isRecord(value)) throw new Error(`${label} must be a JSON object.`);
  return value;
};

const requireNonBlankString = (value: unknown, label: string) => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${label} must be a non-blank string.`);
  }
};

const requireAbsent = (value: unknown, label: string) => {
  if (value != null) throw new Error(`${label} is not valid for this selector scope.`);
};

const requireObjectData = (value: unknown, label: string) => {
  if (!isRecord(value)) throw new Error(`${label} must be a JSON object.`);
};

const rejectUnexpectedKeys = (
  value: Record<string, unknown>,
  allowedKeys: string[],
  label: string,
) => {
  const unexpected = Object.keys(value).find((key) => !allowedKeys.includes(key));
  if (unexpected) throw new Error(`${label} contains an unsupported field.`);
};

const validateConsumerSelector = (value: unknown, label: string) => {
  const consumer = requireRecord(value, label);
  rejectUnexpectedKeys(
    consumer,
    ['scope', 'step_id', 'call_step_id', 'subflow_id', 'module_step_id'],
    label,
  );
  if (consumer.scope === 'root') {
    requireNonBlankString(consumer.step_id, `${label}.step_id`);
    requireAbsent(consumer.call_step_id, `${label}.call_step_id`);
    requireAbsent(consumer.subflow_id, `${label}.subflow_id`);
    requireAbsent(consumer.module_step_id, `${label}.module_step_id`);
    return;
  }
  if (consumer.scope === 'local_subflow') {
    requireNonBlankString(consumer.call_step_id, `${label}.call_step_id`);
    requireNonBlankString(consumer.subflow_id, `${label}.subflow_id`);
    requireNonBlankString(consumer.module_step_id, `${label}.module_step_id`);
    requireAbsent(consumer.step_id, `${label}.step_id`);
    return;
  }
  throw new Error(`${label}.scope must be root or local_subflow.`);
};

const validateOptionalNonBlankString = (value: unknown, label: string) => {
  if (value != null) requireNonBlankString(value, label);
};

const validateInputOverride = (value: unknown, index: number) => {
  const label = `Synthetic input overrides[${index}]`;
  const override = requireRecord(value, label);
  rejectUnexpectedKeys(
    override,
    ['consumer', 'input_group_key', 'input_index', 'payload_kind', 'schema_id', 'schema_version', 'fixture_label', 'data'],
    label,
  );
  validateConsumerSelector(override.consumer, `${label}.consumer`);
  requireNonBlankString(override.input_group_key, `${label}.input_group_key`);
  if (!Number.isInteger(override.input_index) || (override.input_index as number) < 0) {
    throw new Error(`${label}.input_index must be a non-negative integer.`);
  }
  if (!isRecord(override.data) && !Array.isArray(override.data)) {
    throw new Error(`${label}.data must be a JSON object or array.`);
  }
  validateOptionalNonBlankString(override.schema_id, `${label}.schema_id`);
  validateOptionalNonBlankString(override.fixture_label, `${label}.fixture_label`);
  if (override.payload_kind === 'schema_validated') {
    requireNonBlankString(override.schema_id, `${label}.schema_id`);
    if (!Number.isInteger(override.schema_version) || (override.schema_version as number) < 1) {
      throw new Error(`${label}.schema_version must be a positive integer.`);
    }
    if (override.fixture_label != null) {
      throw new Error(`${label} schema_validated overrides do not accept fixture_label.`);
    }
    return;
  }
  if (override.payload_kind === 'opaque_json' || override.payload_kind === 'producer_validated') {
    if (override.payload_kind === 'opaque_json') requireObjectData(override.data, `${label}.data`);
    requireNonBlankString(override.fixture_label, `${label}.fixture_label`);
    if (override.schema_id != null || override.schema_version != null) {
      throw new Error(`${label} ${override.payload_kind} overrides do not accept schema identity.`);
    }
    return;
  }
  throw new Error(`${label}.payload_kind must be schema_validated, producer_validated or opaque_json.`);
};

const validateTriggerOverride = (value: unknown, index: number) => {
  const label = `Synthetic trigger overrides[${index}]`;
  const override = requireRecord(value, label);
  rejectUnexpectedKeys(override, ['trigger_step_id', 'trigger_id', 'data'], label);
  requireNonBlankString(override.trigger_step_id, `${label}.trigger_step_id`);
  requireNonBlankString(override.trigger_id, `${label}.trigger_id`);
  requireObjectData(override.data, `${label}.data`);
};

const rejectDuplicateOverrides = (
  overrides: unknown[],
  label: string,
  identity: (override: Record<string, unknown>) => string,
) => {
  const seen = new Map<string, number>();
  overrides.forEach((value, index) => {
    const key = identity(value as Record<string, unknown>);
    const previousIndex = seen.get(key);
    if (previousIndex != null) {
      throw new Error(`${label}[${index}] duplicates ${label}[${previousIndex}].`);
    }
    seen.set(key, index);
  });
};

export const parseSyntheticFixtureOverrides = (
  inputOverridesText: string,
  triggerOverridesText: string,
) => {
  const inputOverrides = parseOverrideArray(inputOverridesText, 'Synthetic input overrides');
  const triggerOverrides = parseOverrideArray(triggerOverridesText, 'Synthetic trigger overrides');
  if (inputOverrides.length === 0 && triggerOverrides.length === 0) {
    throw new Error('Add at least one synthetic input or Manual Trigger override.');
  }
  inputOverrides.forEach(validateInputOverride);
  triggerOverrides.forEach(validateTriggerOverride);
  rejectDuplicateOverrides(
    inputOverrides,
    'Synthetic input overrides',
    (override) => fixtureInputSlotIdentity(
      override.consumer,
      override.input_group_key,
      override.input_index,
    ),
  );
  rejectDuplicateOverrides(
    triggerOverrides,
    'Synthetic trigger overrides',
    (override) => JSON.stringify([
      String(override.trigger_step_id).trim(),
      String(override.trigger_id).trim(),
    ]),
  );
  return { inputOverrides, triggerOverrides };
};
